// Single 5-year acquisition pro forma — powers the Financial Model page AND the
// listing page's 5-Yr Model tab. Same rule as lib/sbaMath.ts and
// lib/valuationMath.ts: never re-derive this math elsewhere.
//
// Loan sizing, the guaranty fee and SBA eligibility come from computeSba, so
// Year 1 debt service and DSCR here always match the SBA Calculator for the
// same deal. This file only adds what the SBA model doesn't do: growth, taxes,
// capex, the seller note over time, a cash reserve, the exit, and the returns.

import { SBA_MIN_DSCR, computeSba, type CashFlowBasis, type SbaResult } from "./sbaMath.ts";

export const HOLD_YEARS = 5;
/** Acquired goodwill / intangibles amortize over 15 years for tax (IRC §197). */
export const TAX_AMORTIZATION_YEARS = 15;
/** Equipment / FF&E — straight-line over the 7-year MACRS class (no bonus depreciation). */
export const EQUIPMENT_DEPRECIATION_YEARS = 7;
/** A carried-forward loss may offset at most 80% of a later year's taxable income. */
export const NOL_LIMIT_SHARE = 0.8;
export const MAX_DOLLARS = 1_000_000_000;

export interface ModelInputs {
  purchasePrice: number;
  /** Financed into the project, as in the SBA model. */
  closingCostsPct: number;
  /** Cash financed with the deal and held as a reserve: it covers shortfall years, and what's left returns at exit. */
  workingCapital: number;
  /** Buyer cash, as a % of total project cost (the SBA's 10% base). */
  downPaymentPct: number;
  /** Seller note, as a % of purchase price. */
  sellerNotePct: number;
  sellerNoteRate: number;
  sellerNoteTermYears: number;
  /** No payments while the SBA loan is outstanding; interest accrues and is settled at exit. */
  sellerNoteStandby: boolean;
  interestRate: number;
  loanTermYears: number;

  /** Revenue in the last 12 months (the listing's figure). */
  revenue: number;
  /** Year 1 = trailing revenue grown one year, instead of trailing revenue flat. */
  growYear1: boolean;
  revenueGrowthPct: number;
  /** "ebitda": margin is after a manager's salary. "sde": margin includes the owner's pay, and ownerSalary comes off. */
  cashFlowBasis: CashFlowBasis;
  /** Margin on the chosen basis (EBITDA or SDE), % of revenue. Can be negative. */
  ebitdaMarginPct: number;
  /** What the owner-operator pays themselves — subtracted from SDE only. */
  ownerSalary: number;
  /** Maintenance capex, % of revenue. */
  capexPct: number;
  /** Combined federal + state rate on taxable income. */
  taxRatePct: number;
  /** Share of the price allocated to equipment / FF&E (7-yr depreciation); the rest is goodwill (15-yr). */
  equipmentPct: number;

  /** One bad year: 0 = none, else the year (1–5) it hits. */
  shockYear: number;
  /** Revenue lost that year, % of the trend. */
  shockRevenuePct: number;
  /** Margin points lost that year (fixed costs don't shrink with revenue). */
  shockMarginPts: number;

  /** Exit price as a multiple of Year 5 EBITDA. */
  exitMultiple: number;
  /** Broker, legal and other selling costs at exit, % of exit price. */
  exitCostsPct: number;
}

export const MODEL_DEFAULTS: ModelInputs = {
  purchasePrice: 1_100_000,
  closingCostsPct: 3,
  workingCapital: 0,
  downPaymentPct: 10,
  sellerNotePct: 10,
  sellerNoteRate: 6,
  sellerNoteTermYears: 5,
  sellerNoteStandby: true,
  interestRate: 11.5,
  loanTermYears: 10,
  revenue: 1_300_000,
  growYear1: false,
  revenueGrowthPct: 6,
  cashFlowBasis: "ebitda",
  ebitdaMarginPct: 22,
  ownerSalary: 80_000,
  capexPct: 1.5,
  taxRatePct: 25,
  equipmentPct: 10,
  shockYear: 0,
  shockRevenuePct: 20,
  shockMarginPts: 5,
  exitMultiple: 3.5,
  exitCostsPct: 8,
};

export interface ModelYear {
  year: number;
  revenue: number;
  /** SDE when the basis is SDE; equals ebitda on the EBITDA basis. */
  earnings: number;
  ebitda: number;
  capex: number;
  sbaInterest: number;
  sbaPrincipal: number;
  sbaDebtService: number;
  /** Cash paid on the seller note this year (0 while on standby). */
  noteDebtService: number;
  noteInterest: number;
  totalDebtService: number;
  /** Goodwill amortization + equipment depreciation. */
  taxDeductions: number;
  /** Carried-forward loss used against this year's income. */
  nolUsed: number;
  taxes: number;
  /** EBITDA after capex, taxes and every debt payment. */
  freeCashFlow: number;
  /** Pulled from the working-capital reserve to cover a negative year. */
  reserveDraw: number;
  /** Cash the buyer has to put in beyond the reserve (0 in a normal year). */
  shortfall: number;
  /** What actually reaches (or leaves) the buyer's pocket this year. */
  buyerCash: number;
  cumulativeBuyerCash: number;
  /** EBITDA ÷ cash debt service — the SBA Calculator's measure. null when nothing is owed. */
  dscr: number | null;
  /** (EBITDA − capex − taxes) ÷ debt service — closer to how lenders underwrite. */
  lenderDscr: number | null;
  sbaBalance: number;
  noteBalance: number;
  shocked: boolean;
}

export interface ModelExit {
  exitValue: number;
  exitCosts: number;
  sbaPayoff: number;
  notePayoff: number;
  /** Unused working-capital reserve, returned at the sale. */
  reserveReturned: number;
  /** What the buyer walks away with at the sale. Can be negative. */
  netProceeds: number;
}

export type ReturnOutcome = "profit" | "loss" | "total-loss";

export interface ModelResult {
  sba: SbaResult;
  equity: number;
  years: ModelYear[];
  exit: ModelExit;
  /** Buyer cash flows, t0 = −equity … t5 = buyer cash + net proceeds. */
  cashFlows: number[];
  /** null when the cash flows never change sign (no meaningful IRR). */
  irr: number | null;
  /** Everything returned ÷ everything put in (down payment + shortfalls). null with nothing in. */
  moic: number | null;
  cashOnCashY1: number | null;
  /** First year cumulative buyer cash covers the equity (ignores the exit). null = not within the hold. */
  paybackYear: number | null;
  minDscr: number | null;
  minLenderDscr: number | null;
  /** Cash the buyer must inject over the hold beyond the down payment. */
  totalShortfall: number;
  totalProfit: number;
  /** "total-loss" = less than nothing comes back; "loss" = some, but under what went in. */
  outcome: ReturnOutcome;
  /** Price ÷ Year 1 EBITDA. */
  entryMultiple: number | null;
}

/** Monthly-step loan simulator, so a standby note can accrue then amortize. */
function loanYears(
  principal: number,
  ratePct: number,
  termYears: number,
  years: number,
  standbyMonths = 0,
): { interest: number; paidInterest: number; principal: number; payment: number; balance: number }[] {
  const r = ratePct / 100 / 12;
  let balance = principal;
  let pmt = 0;
  let amortizing = false;
  const n = termYears * 12;
  const out = [];
  for (let y = 0; y < years; y++) {
    let interest = 0;
    let paidInterest = 0;
    let prin = 0;
    let paid = 0;
    for (let m = 0; m < 12; m++) {
      const month = y * 12 + m;
      if (balance <= 0.005) continue;
      const int = balance * r;
      interest += int;
      if (month < standbyMonths) {
        balance += int; // accrues, nothing paid
        continue;
      }
      if (!amortizing) {
        amortizing = true;
        pmt = r === 0 ? balance / n : (balance * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      }
      const p = Math.min(balance, pmt - int);
      paidInterest += int;
      prin += p;
      paid += int + p;
      balance -= p;
    }
    out.push({ interest, paidInterest, principal: prin, payment: paid, balance: Math.max(0, balance) });
  }
  return out;
}

/**
 * IRR: scan for the NPV sign change nearest 0%, then bisect. A plain bracket
 * misses deals whose exit leaves the buyer owing money — those flows change
 * sign twice (−, +, +, −), so NPV is negative at both ends of a wide bracket.
 */
export function irr(flows: number[]): number | null {
  const hasNeg = flows.some((f) => f < 0);
  const hasPos = flows.some((f) => f > 0);
  if (!hasNeg || !hasPos) return null;
  const npv = (rate: number) => flows.reduce((s, f, t) => s + f / Math.pow(1 + rate, t), 0);
  // Rates from −99% to +10,000%, ordered by distance from 0 so the economically
  // sensible root wins when there are two.
  const grid: number[] = [];
  for (let r = -0.99; r <= 100; r = r < 1 ? r + 0.01 : r * 1.05) grid.push(r);
  const brackets: [number, number][] = [];
  for (let k = 1; k < grid.length; k++) {
    if (npv(grid[k - 1]) * npv(grid[k]) <= 0) brackets.push([grid[k - 1], grid[k]]);
  }
  if (!brackets.length) return null;
  brackets.sort((a, b) => Math.min(Math.abs(a[0]), Math.abs(a[1])) - Math.min(Math.abs(b[0]), Math.abs(b[1])));
  let [lo, hi] = brackets[0];
  const loSign = Math.sign(npv(lo));
  for (let k = 0; k < 100; k++) {
    const mid = (lo + hi) / 2;
    if (Math.sign(npv(mid)) === loSign) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Year-1 earnings on the chosen basis, and the EBITDA lenders see after the owner's pay. */
function yearOneEarnings(i: ModelInputs): { earnings: number; ebitda: number } {
  const revenue = i.revenue * (i.growYear1 ? 1 + i.revenueGrowthPct / 100 : 1);
  const earnings = revenue * (i.ebitdaMarginPct / 100);
  return { earnings, ebitda: i.cashFlowBasis === "sde" ? earnings - i.ownerSalary : earnings };
}

export function computeModel(i: ModelInputs): ModelResult {
  const y1 = yearOneEarnings(i);
  // Loan sizing + eligibility from the one SBA model, on the same basis.
  const sba = computeSba({
    askingPrice: i.purchasePrice,
    closingCostsPct: i.closingCostsPct,
    workingCapital: i.workingCapital,
    downPaymentPct: i.downPaymentPct,
    sellerNotePct: i.sellerNotePct,
    sellerNoteRate: i.sellerNoteRate,
    sellerNoteTermYears: i.sellerNoteTermYears,
    sellerNoteStandby: i.sellerNoteStandby,
    interestRate: i.interestRate,
    loanTermYears: i.loanTermYears,
    // Business-only deal: a term past 10 years is flagged for review, as in the SBA Calculator.
    includesRealEstate: false,
    cashFlowBasis: i.cashFlowBasis,
    cashFlow: y1.earnings,
    ownerSalary: i.ownerSalary,
    otherDebtPayments: 0,
  });

  const sbaRows = loanYears(sba.loanAmount, i.interestRate, i.loanTermYears, HOLD_YEARS);
  // Full standby lasts while the SBA loan is outstanding.
  const standbyMonths = i.sellerNoteStandby && sba.loanAmount > 0 ? i.loanTermYears * 12 : 0;
  const noteRows = loanYears(sba.sellerNote, i.sellerNoteRate, i.sellerNoteTermYears, HOLD_YEARS, standbyMonths);

  const equipment = i.purchasePrice * (Math.min(100, Math.max(0, i.equipmentPct)) / 100);
  const deductions = (i.purchasePrice - equipment) / TAX_AMORTIZATION_YEARS + equipment / EQUIPMENT_DEPRECIATION_YEARS;
  const growthOffset = i.growYear1 ? 1 : 0;

  const years: ModelYear[] = [];
  let reserve = i.workingCapital;
  let nol = 0;
  let cumulative = 0;
  for (let y = 0; y < HOLD_YEARS; y++) {
    const shocked = i.shockYear === y + 1;
    const trend = i.revenue * Math.pow(1 + i.revenueGrowthPct / 100, y + growthOffset);
    const revenue = shocked ? trend * (1 - i.shockRevenuePct / 100) : trend;
    const margin = i.ebitdaMarginPct - (shocked ? i.shockMarginPts : 0);
    const earnings = revenue * (margin / 100);
    const ebitda = i.cashFlowBasis === "sde" ? earnings - i.ownerSalary : earnings;
    const capex = revenue * (i.capexPct / 100);
    const s = sbaRows[y];
    const n = noteRows[y];
    const totalDebtService = s.payment + n.payment;

    // Interest is deductible as paid; accrued standby interest isn't deducted (conservative).
    let taxable = ebitda - s.interest - n.paidInterest - deductions;
    let nolUsed = 0;
    if (taxable < 0) {
      nol += -taxable;
      taxable = 0;
    } else if (nol > 0) {
      nolUsed = Math.min(nol, taxable * NOL_LIMIT_SHARE);
      nol -= nolUsed;
      taxable -= nolUsed;
    }
    const taxes = taxable * (i.taxRatePct / 100);
    const freeCashFlow = ebitda - capex - taxes - totalDebtService;

    let reserveDraw = 0;
    let shortfall = 0;
    if (freeCashFlow < 0) {
      reserveDraw = Math.min(reserve, -freeCashFlow);
      reserve -= reserveDraw;
      shortfall = -freeCashFlow - reserveDraw;
    }
    const buyerCash = freeCashFlow + reserveDraw;
    cumulative += buyerCash;

    years.push({
      year: y + 1,
      revenue,
      earnings,
      ebitda,
      capex,
      sbaInterest: s.interest,
      sbaPrincipal: s.principal,
      sbaDebtService: s.payment,
      noteDebtService: n.payment,
      noteInterest: n.interest,
      totalDebtService,
      taxDeductions: deductions,
      nolUsed,
      taxes,
      freeCashFlow,
      reserveDraw,
      shortfall,
      buyerCash,
      cumulativeBuyerCash: cumulative,
      dscr: totalDebtService > 0 ? ebitda / totalDebtService : null,
      lenderDscr: totalDebtService > 0 ? (ebitda - capex - taxes) / totalDebtService : null,
      sbaBalance: s.balance,
      noteBalance: n.balance,
      shocked,
    });
  }

  const last = years[HOLD_YEARS - 1];
  const exitValue = Math.max(0, last.ebitda * i.exitMultiple);
  const exitCosts = exitValue * (i.exitCostsPct / 100);
  const exit: ModelExit = {
    exitValue,
    exitCosts,
    sbaPayoff: last.sbaBalance,
    notePayoff: last.noteBalance,
    reserveReturned: reserve,
    netProceeds: exitValue - exitCosts - last.sbaBalance - last.noteBalance + reserve,
  };

  const equity = sba.downPayment;
  const cashFlows = [-equity, ...years.map((y, k) => y.buyerCash + (k === HOLD_YEARS - 1 ? exit.netProceeds : 0))];
  const totalShortfall = years.reduce((s, y) => s + y.shortfall, 0);
  const net = cashFlows.reduce((a, b) => a + b, 0);
  const putIn = cashFlows.filter((f) => f < 0).reduce((a, b) => a - b, 0);
  const gotBack = cashFlows.filter((f) => f > 0).reduce((a, b) => a + b, 0);
  const paybackIdx = years.findIndex((y) => y.cumulativeBuyerCash >= equity);
  const nonNull = (xs: (number | null)[]) => xs.filter((d): d is number => d !== null);
  const dscrs = nonNull(years.map((y) => y.dscr));
  const lenderDscrs = nonNull(years.map((y) => y.lenderDscr));

  return {
    sba,
    equity,
    years,
    exit,
    cashFlows,
    irr: irr(cashFlows),
    moic: putIn > 0 ? gotBack / putIn : null,
    cashOnCashY1: equity > 0 ? years[0].buyerCash / equity : null,
    paybackYear: equity > 0 && paybackIdx >= 0 ? paybackIdx + 1 : null,
    minDscr: dscrs.length ? Math.min(...dscrs) : null,
    minLenderDscr: lenderDscrs.length ? Math.min(...lenderDscrs) : null,
    totalShortfall,
    totalProfit: net,
    outcome: net >= 0 ? "profit" : gotBack <= 0 ? "total-loss" : "loss",
    entryMultiple: y1.ebitda > 0 ? i.purchasePrice / y1.ebitda : null,
  };
}

// ── Scenarios & sensitivity ──────────────────────────────────────────────────
export type ScenarioKey = "stress" | "downside" | "base" | "upside";

interface ScenarioShift {
  label: string;
  growth: number;
  margin: number;
  multiple: number;
  /** SBA loans float with prime — the rate moves in the bad cases. */
  rate: number;
  /** Bad year applied when the inputs don't already set one. */
  shock?: { year: number; revenuePct: number; marginPts: number };
}

export const SCENARIO_SHIFTS: Record<ScenarioKey, ScenarioShift> = {
  stress: { label: "Stress", growth: -6, margin: -4, multiple: -1, rate: 3, shock: { year: 2, revenuePct: 25, marginPts: 6 } },
  downside: { label: "Downside", growth: -6, margin: -4, multiple: -0.5, rate: 2 },
  base: { label: "Base", growth: 0, margin: 0, multiple: 0, rate: 0 },
  upside: { label: "Upside", growth: 3, margin: 2, multiple: 0.5, rate: 0 },
};

export const SCENARIO_ORDER: ScenarioKey[] = ["stress", "downside", "base", "upside"];

export function describeScenario(key: ScenarioKey): string {
  const s = SCENARIO_SHIFTS[key];
  const signed = (n: number) => (n > 0 ? `+${n}` : String(n));
  const parts = [
    `growth ${signed(s.growth)} pts`,
    `margin ${signed(s.margin)} pts`,
    `exit multiple ${signed(s.multiple)}×`,
  ];
  if (s.rate) parts.push(`SBA rate ${signed(s.rate)} pts`);
  if (s.shock) parts.push(`a Year ${s.shock.year} slump (−${s.shock.revenuePct}% revenue, −${s.shock.marginPts} pts margin) unless you set your own`);
  return parts.join(", ");
}

export function scenarioInputs(i: ModelInputs, key: ScenarioKey): ModelInputs {
  const s = SCENARIO_SHIFTS[key];
  const out: ModelInputs = {
    ...i,
    revenueGrowthPct: i.revenueGrowthPct + s.growth,
    ebitdaMarginPct: i.ebitdaMarginPct + s.margin,
    exitMultiple: Math.max(0.5, i.exitMultiple + s.multiple),
    interestRate: Math.max(0, i.interestRate + s.rate),
  };
  if (s.shock && i.shockYear === 0)
    Object.assign(out, { shockYear: s.shock.year, shockRevenuePct: s.shock.revenuePct, shockMarginPts: s.shock.marginPts });
  return out;
}

export const SENSITIVITY_MULTIPLE_STEPS = [-1, -0.5, 0, 0.5, 1];
export const SENSITIVITY_GROWTH_STEPS = [-4, -2, 0, 2, 4];

/** IRR grid: rows = exit multiple, columns = revenue growth, both around the inputs. */
export function sensitivityGrid(i: ModelInputs): { multiple: number; growth: number; irr: number | null; outcome: ReturnOutcome }[][] {
  return SENSITIVITY_MULTIPLE_STEPS.map((dm) =>
    SENSITIVITY_GROWTH_STEPS.map((dg) => {
      const multiple = Math.max(0.5, i.exitMultiple + dm);
      const growth = i.revenueGrowthPct + dg;
      const r = computeModel({ ...i, exitMultiple: multiple, revenueGrowthPct: growth });
      return { multiple, growth, irr: r.irr, outcome: r.outcome };
    }),
  );
}

/** IRR as display text — a deal that loses everything says so instead of "n/a". */
export function irrLabel(r: Pick<ModelResult, "irr" | "outcome">): string {
  if (r.irr !== null) return `${(r.irr * 100).toFixed(1)}%`;
  return r.outcome === "total-loss" ? "Total loss" : r.outcome === "loss" ? "Loss" : "n/a";
}

// ── Insights — rule-based reads of the numbers, never invented figures ──────
export type InsightTone = "good" | "warn" | "risk" | "info";

export function modelInsights(i: ModelInputs, r: ModelResult): { tone: InsightTone; text: string }[] {
  const out: { tone: InsightTone; text: string }[] = [];
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  const money = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 2 }).format(n);

  if (r.outcome !== "profit") {
    out.push({
      tone: "risk",
      text: `This deal loses money: you put in ${money(r.equity + r.totalShortfall)} and get back ${money(r.cashFlows.filter((f) => f > 0).reduce((a, b) => a + b, 0))} over ${HOLD_YEARS} years, including the sale.`,
    });
  } else if (r.irr !== null && r.moic !== null) {
    out.push({
      tone: r.irr >= 0.25 ? "good" : r.irr >= 0.15 ? "info" : "warn",
      text: `Exit at ${i.exitMultiple.toFixed(1)}× Year ${HOLD_YEARS} EBITDA → ${money(r.exit.exitValue)}. After selling costs and paying off ${money(r.exit.sbaPayoff + r.exit.notePayoff)} of remaining debt you net ${money(r.exit.netProceeds)}: ${r.moic.toFixed(1)}× what you put in (${pct(r.irr)} IRR).`,
    });
  }

  if (r.totalShortfall > 0)
    out.push({
      tone: "risk",
      text: `Cash flow doesn't cover the debt in Year ${r.years.filter((y) => y.shortfall > 0).map((y) => y.year).join(", ")} — you'd have to inject ${money(r.totalShortfall)} on top of the down payment${i.workingCapital > 0 ? " after the working-capital reserve runs out" : ". Financing a working-capital reserve would cushion it"}.`,
    });

  const weak = r.years.filter((y) => y.lenderDscr !== null && y.lenderDscr < SBA_MIN_DSCR);
  if (weak.length)
    out.push({
      tone: "risk",
      text: `After capex and taxes, debt coverage falls below the ${SBA_MIN_DSCR.toFixed(2)}× lender minimum in Year ${weak.map((y) => y.year).join(", ")} — expect a lender to push back on price or structure.`,
    });
  else if (r.minLenderDscr !== null)
    out.push({
      tone: r.minLenderDscr >= 1.5 ? "good" : "info",
      text: `Lender-style DSCR (after capex and taxes) holds at ${r.minLenderDscr.toFixed(2)}× or better every year. On plain EBITDA it's ${r.minDscr?.toFixed(2)}×.`,
    });

  // Floating-rate exposure: same deal, prime up 2 points.
  if (r.sba.loanAmount > 0) {
    const shocked = computeModel({ ...i, interestRate: i.interestRate + 2 });
    if (shocked.minLenderDscr !== null && r.minLenderDscr !== null)
      out.push({
        tone: shocked.minLenderDscr < SBA_MIN_DSCR ? "warn" : "info",
        text: `SBA rates float with prime. If rates rise 2 points, annual debt service goes up ${money(shocked.years[0].sbaDebtService - r.years[0].sbaDebtService)} and the lowest lender DSCR drops to ${shocked.minLenderDscr.toFixed(2)}×.`,
      });
  }

  if (i.shockYear > 0) {
    const y = r.years[i.shockYear - 1];
    out.push({
      tone: y.buyerCash < 0 ? "risk" : "info",
      text: `The Year ${i.shockYear} slump drops EBITDA to ${money(y.ebitda)}${y.lenderDscr !== null ? ` (lender DSCR ${y.lenderDscr.toFixed(2)}×)` : ""}.`,
    });
  }

  if (r.entryMultiple !== null && i.exitMultiple > r.entryMultiple + 0.25)
    out.push({
      tone: "warn",
      text: `The model exits at ${i.exitMultiple.toFixed(1)}× but you buy at ${r.entryMultiple.toFixed(1)}× — multiple expansion is doing part of the work. Check the return at the entry multiple in the sensitivity grid.`,
    });

  const gotBack = r.cashFlows.slice(1).reduce((a, b) => a + b, 0);
  if (r.outcome === "profit" && gotBack > 0 && r.exit.netProceeds / gotBack > 0.6)
    out.push({ tone: "info", text: `${Math.round((r.exit.netProceeds / gotBack) * 100)}% of the total return comes from the sale — the deal leans on the exit more than on cash flow.` });

  if (i.cashFlowBasis === "sde")
    out.push({ tone: "info", text: `These returns are on top of the ${money(i.ownerSalary)} a year you pay yourself to run it.` });

  if (i.sellerNoteStandby && r.sba.sellerNote > 0 && r.sba.loanAmount > 0)
    out.push({ tone: "info", text: `The seller note is on full standby: no payments for the hold, but it grows to ${money(r.exit.notePayoff)} with accrued interest and is settled from exit proceeds.` });

  if (r.paybackYear !== null) out.push({ tone: "good", text: `Cash flow alone pays back your down payment by Year ${r.paybackYear}.` });

  if (r.sba.status === "review") out.push({ tone: "warn", text: `SBA 7(a): ${r.sba.reasons.join(" · ")}.` });
  return out;
}

// ── Scenario ↔ query string (Share link / listing hand-off / saved scenarios) ─
export function toModelQuery(i: ModelInputs): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(i)) q.set(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
  return q.toString();
}

/** Keep only known keys with the right type — for query strings and stored JSON alike. */
export function sanitizeModelInputs(raw: Record<string, unknown>): Partial<ModelInputs> {
  const out: Record<string, unknown> = {};
  for (const [k, def] of Object.entries(MODEL_DEFAULTS)) {
    const v = raw[k];
    if (v === undefined || v === null || v === "") continue;
    if (typeof def === "number" && Number.isFinite(Number(v))) out[k] = Number(v);
    else if (typeof def === "boolean") {
      if (v === true || v === "1") out[k] = true;
      else if (v === false || v === "0") out[k] = false;
    } else if (k === "cashFlowBasis" && (v === "sde" || v === "ebitda")) out[k] = v;
  }
  return out as Partial<ModelInputs>;
}

/** Lenient: unknown or malformed params are ignored, so a stale link still loads. */
export function parseModelQuery(search: string): Partial<ModelInputs> {
  const q = new URLSearchParams(search);
  const raw: Record<string, string> = {};
  for (const k of Object.keys(MODEL_DEFAULTS)) {
    const v = q.get(k)?.trim();
    if (v) raw[k] = v;
  }
  return sanitizeModelInputs(raw);
}

/** Hand the deal's financing terms to the SBA Calculator. */
export function toSbaQueryFromModel(i: ModelInputs): string {
  return new URLSearchParams({
    askingPrice: String(i.purchasePrice),
    closingCostsPct: String(i.closingCostsPct),
    workingCapital: String(i.workingCapital),
    downPaymentPct: String(i.downPaymentPct),
    sellerNotePct: String(i.sellerNotePct),
    sellerNoteRate: String(i.sellerNoteRate),
    sellerNoteTermYears: String(i.sellerNoteTermYears),
    sellerNoteStandby: i.sellerNoteStandby ? "1" : "0",
    interestRate: String(i.interestRate),
    loanTermYears: String(i.loanTermYears),
    cashFlowBasis: i.cashFlowBasis,
    cashFlow: String(Math.round(yearOneEarnings(i).earnings)),
    ownerSalary: String(i.ownerSalary),
  }).toString();
}

/** Plain-text summary for a Deal CRM note (max 4,000 chars server-side). */
export function modelSummaryText(i: ModelInputs, r: ModelResult, scenarioLabel: string, link: string): string {
  const money = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
  const x = (n: number | null) => (n === null ? "n/a" : `${n.toFixed(2)}x`);
  return [
    `5-year financial model (${scenarioLabel} case)`,
    `Price ${money(i.purchasePrice)} · ${i.downPaymentPct}% down (${money(r.equity)}) · SBA ${money(r.sba.loanAmount)} at ${i.interestRate}% / ${i.loanTermYears} yrs · seller note ${money(r.sba.sellerNote)}`,
    `Revenue ${money(i.revenue)} growing ${i.revenueGrowthPct}%/yr · ${i.cashFlowBasis === "sde" ? "SDE" : "EBITDA"} margin ${i.ebitdaMarginPct}% · exit ${i.exitMultiple}x`,
    `IRR ${irrLabel(r)} · equity multiple ${x(r.moic)} · min lender DSCR ${x(r.minLenderDscr)} · net exit proceeds ${money(r.exit.netProceeds)}`,
    r.totalShortfall > 0 ? `Cash shortfall over the hold: ${money(r.totalShortfall)}` : "",
    link,
  ].filter(Boolean).join("\n");
}

// ── Export ───────────────────────────────────────────────────────────────────
export function modelCsv(i: ModelInputs, r: ModelResult, scenarioLabel = "Base"): string {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const round = (n: number) => Math.round(n);
  const line = (label: string, vals: (string | number)[]) => [label, ...vals].map(esc).join(",");
  const ys = r.years;
  const dscr = (d: number | null) => (d === null ? "" : d.toFixed(2));
  const sde = i.cashFlowBasis === "sde";
  const rows = [
    line("Scenario", [scenarioLabel]),
    "",
    line("5-Year Pro Forma", ys.map((y) => `Year ${y.year}${y.shocked ? " (slump)" : ""}`)),
    line("Revenue", ys.map((y) => round(y.revenue))),
    ...(sde ? [line("SDE", ys.map((y) => round(y.earnings))), line("Owner salary", ys.map(() => -round(i.ownerSalary)))] : []),
    line("EBITDA", ys.map((y) => round(y.ebitda))),
    line("Capex", ys.map((y) => -round(y.capex))),
    line("Taxes", ys.map((y) => -round(y.taxes))),
    line("SBA debt service", ys.map((y) => -round(y.sbaDebtService))),
    line("Seller note payments", ys.map((y) => -round(y.noteDebtService))),
    line("Free cash flow", ys.map((y) => round(y.freeCashFlow))),
    line("Reserve draw", ys.map((y) => round(y.reserveDraw))),
    line("Buyer cash injection needed", ys.map((y) => round(y.shortfall))),
    line("Cash to buyer", ys.map((y) => round(y.buyerCash))),
    line("Cumulative cash to buyer", ys.map((y) => round(y.cumulativeBuyerCash))),
    line("DSCR (EBITDA)", ys.map((y) => dscr(y.dscr))),
    line("Lender DSCR (after capex & taxes)", ys.map((y) => dscr(y.lenderDscr))),
    line("SBA balance (year end)", ys.map((y) => round(y.sbaBalance))),
    line("Seller note balance (year end)", ys.map((y) => round(y.noteBalance))),
    "",
    line("Purchase price", [i.purchasePrice]),
    line("Working capital reserve (financed)", [i.workingCapital]),
    line("Buyer equity (down payment)", [round(r.equity)]),
    line("SBA loan (incl. guaranty fee)", [round(r.sba.loanAmount)]),
    line("Seller note", [round(r.sba.sellerNote)]),
    line("SBA rate / term", [`${i.interestRate}% / ${i.loanTermYears} yrs`]),
    line("Year 1 revenue basis", [i.growYear1 ? "Trailing revenue + one year of growth" : "Trailing revenue"]),
    line("Revenue growth", [`${i.revenueGrowthPct}%`]),
    line(`${sde ? "SDE" : "EBITDA"} margin`, [`${i.ebitdaMarginPct}%`]),
    line("Capex (% of revenue)", [`${i.capexPct}%`]),
    line("Tax rate", [`${i.taxRatePct}%`]),
    line("Price allocated to equipment", [`${i.equipmentPct}%`]),
    line("Exit multiple (x Year 5 EBITDA)", [i.exitMultiple]),
    line("Exit value", [round(r.exit.exitValue)]),
    line("Exit costs", [-round(r.exit.exitCosts)]),
    line("Debt payoff at exit", [-round(r.exit.sbaPayoff + r.exit.notePayoff)]),
    line("Reserve returned at exit", [round(r.exit.reserveReturned)]),
    line("Net exit proceeds", [round(r.exit.netProceeds)]),
    "",
    line("IRR (5-year)", [irrLabel(r)]),
    line("Equity multiple (MOIC)", [r.moic === null ? "n/a" : r.moic.toFixed(2)]),
    line("Cash-on-cash (Year 1)", [r.cashOnCashY1 === null ? "n/a" : `${(r.cashOnCashY1 * 100).toFixed(1)}%`]),
    line("Minimum lender DSCR", [dscr(r.minLenderDscr) || "n/a"]),
    line("Total cash injections beyond down payment", [round(r.totalShortfall)]),
    line("Total profit", [round(r.totalProfit)]),
  ];
  return rows.join("\n");
}
